import { defineComponent as _defineComponent } from 'vue'
import {
  computed, nextTick, onBeforeUnmount, ref, watch,
} from 'vue';
import { onClickOutside, useWindowSize } from '@vueuse/core';
import BCXPopup from '@/components/molecules/BCXPopup.vue';
import BCXButton from '@/components/molecules/BCXButton.vue';
import BCXConfirm from '@/components/molecules/BCXConfirm.vue';
import BCXChevronSwitch from '@/components/molecules/BCXChevronSwitch.vue';
import BCLoadingAnimation from '@/components/molecules/BCLoadingAnimation.vue';
import BCXHelpTextTriggerIcon from '@/components/molecules/BCXHelpTextTriggerIcon.vue';
import BCXInfoCollapsible from '@/components/molecules/BCXInfoCollapsible.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXSearchSelect from '@/components/molecules/BCXSearchSelect.vue';
import EditIcon from '@/components/molecules/EditIcon.vue';
import MarvinInfoBox from '@/views/recommendations/components/MarvinInfoBox.vue';
import AiIcon from '@/components/svg/ai-icon-large.png';
import IconLightbulb from '@/components/svg/profile-lightbulb.svg?inline';
import IconAdd from '@/components/svg/button/plus-primary.svg?inline';
import IconRemove from '@/components/svg/button/x.svg?inline';
import useI18n from '@/mixins/useI18n';
import useTriggerLegalTextsPopup from '@/mixins/useTriggerLegalTextsPopup';
import useFileUpload, { FileUpload } from '@/mixins/useFileUpload';
import usePopupState from '@/state/usePopupState';
import VueI18n from '@/i18n';
import useProfilePdfUploadPopup from '@/views/profile/composables/useProfilePdfUploadPopup';
import useProfilePdfUpload, { ALL_PROFILE_PDF_AREAS, ProfilePdfArea } from '@/views/profile/composables/useProfilePdfUpload';
import { SelectItem } from '@/models/SelectItem';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfilePdfUploadPopup',
  setup(__props) {

const MAX_INSTRUCTION_CHARS = 800;
const ANALYSIS_HINT_ROTATION_INTERVAL_MS = 6000;
const POPUP_WIDTH_PX = 600;
const MAX_PDF_BYTES = 6 * 1024 * 1024;
const POPUP_VIEWPORT_MARGIN_PX = 32;

const { t, locale } = useI18n();
const { setLegalText } = useTriggerLegalTextsPopup();
const { setPdf, showPopup, hidePopup } = usePopupState();
const { isOpen, close } = useProfilePdfUploadPopup();
const {
  fileName,
  fileContents,
  customInstructions,
  outputLanguage,
  selectedProfileAreas,
  isSubmitting,
  isReanalyzing,
  submit,
  cancel,
  reset,
  clearFile,
} = useProfilePdfUpload();

const fileUploadConfig = ref<FileUpload>({
  fileType: { allowedMimeTypes: ['application/pdf'] },
  fileSize: {
    maxFileSizeFromProps: MAX_PDF_BYTES,
    permittedTotalFileSizeFromProps: MAX_PDF_BYTES,
    currentTotalFileSizeFromProps: 0,
  },
});
const {
  upload,
  fileName: pickedFileName,
  fileContents: pickedFileContents,
  mimeType: pickedMimeType,
  error: pickedFileError,
  maxFileSizeMB,
} = useFileUpload(fileUploadConfig);

// Mirror useFileUpload's output into the composable's state.
watch(pickedFileName, (name) => { fileName.value = name; });
watch(pickedFileContents, (contents) => { fileContents.value = contents; });

const isInstructionsExpanded = ref(false);
const isProfileFileHelpOpen = ref(false);
const showProfileAreasDropdown = ref(false);
const charsLeft = computed(() => MAX_INSTRUCTION_CHARS - customInstructions.value.length);
const previewButtonTitle = computed(() => t('buttons.preview').toString());
const clearButtonTitle = computed(() => t('buttons.delete').toString());
const pickFileButtonTitle = computed(() => t('profilePdfUpload.popup.profileFile.label').toString());
const outputLanguageOptions = computed<SelectItem[]>(() => ([
  {
    value: 'Deutsch',
    text: t('profilePdfUpload.popup.outputLanguage.options.german').toString(),
  },
  {
    value: 'Englisch',
    text: t('profilePdfUpload.popup.outputLanguage.options.english').toString(),
  },
]));
const profileAreaItems = computed<Array<{ value: ProfilePdfArea; label: string }>>(() => ([
  { value: 'avatar', label: t('profilePdfUpload.popup.profileAreas.items.avatar').toString() },
  { value: 'basic', label: t('profilePdfUpload.popup.profileAreas.items.basic').toString() },
  { value: 'skills', label: t('profilePdfUpload.popup.profileAreas.items.skills').toString() },
  { value: 'educations', label: t('profilePdfUpload.popup.profileAreas.items.educations').toString() },
  { value: 'certificates', label: t('profilePdfUpload.popup.profileAreas.items.certificates').toString() },
  { value: 'projects', label: t('profilePdfUpload.popup.profileAreas.items.projects').toString() },
]));
const isAllProfileAreasSelected = computed(
  () => selectedProfileAreas.value.length === ALL_PROFILE_PDF_AREAS.length
);
const selectedProfileAreasTitle = computed(() => {
  if (!selectedProfileAreas.value.length) {
    return t('profilePdfUpload.popup.profileAreas.selectedCount', { count: 0 }).toString();
  }
  if (isAllProfileAreasSelected.value) {
    return t('profilePdfUpload.popup.profileAreas.allSelected').toString();
  }
  return t('profilePdfUpload.popup.profileAreas.selectedCount', { count: selectedProfileAreas.value.length }).toString();
});
const selectAllProfileAreas = () => {
  selectedProfileAreas.value = [...ALL_PROFILE_PDF_AREAS];
};
const selectNoProfileAreas = () => {
  selectedProfileAreas.value = [];
};
const toggleProfileArea = (value: ProfilePdfArea) => {
  const nextValues = selectedProfileAreas.value.includes(value)
    ? selectedProfileAreas.value.filter((item) => item !== value)
    : [...selectedProfileAreas.value, value];

  selectedProfileAreas.value = ALL_PROFILE_PDF_AREAS.filter((item) => nextValues.includes(item));
};
const fileErrorText = computed(() => {
  switch (pickedFileError.value) {
    case 'too_big':
      return t('profilePdfUpload.popup.profileFile.errors.tooBig', { maxMb: maxFileSizeMB }).toString();
    case 'wrong_type':
      return t('profilePdfUpload.popup.profileFile.errors.wrongType').toString();
    case 'total_size_exceeded':
      return t('profilePdfUpload.popup.profileFile.errors.totalSizeExceeded', { maxMb: maxFileSizeMB }).toString();
    default:
      return '';
  }
});
const canPreviewSelectedPdf = computed(
  () => !!fileName.value
    && !!fileContents.value
    && pickedMimeType.value === 'application/pdf',
);
const currentAnalysisHintIndex = ref(0);
let analysisHintIntervalId: number | null = null;
const analysisRunningHints = computed<string[]>(() => {
  const localeMessages = VueI18n.getLocaleMessage(locale.value) as Record<string, any>;
  const hints = localeMessages?.profilePdfUpload?.popup?.analysisRunningHints;

  if (!Array.isArray(hints)) return [];

  return hints
    .filter((hint): hint is string => typeof hint === 'string')
    .map((hint) => hint.trim())
    .filter((hint) => hint.length > 0);
});
const currentAnalysisRunningHint = computed(() => {
  if (!analysisRunningHints.value.length) return '';
  return analysisRunningHints.value[currentAnalysisHintIndex.value] ?? analysisRunningHints.value[0];
});

const popupContentElement = ref<HTMLDivElement | null>(null);
const profileAreasElement = ref<HTMLDivElement | null>(null);

const { width: windowWidth, height: windowHeight } = useWindowSize();
const initialXPos = computed(() => Math.max(0, (windowWidth.value - POPUP_WIDTH_PX) / 2));
const initialYPos = ref(POPUP_VIEWPORT_MARGIN_PX);

const updateInitialYPos = async () => {
  if (!isOpen.value) {
    initialYPos.value = POPUP_VIEWPORT_MARGIN_PX;
    return;
  }

  await nextTick();

  const popupHeight = popupContentElement.value?.offsetHeight ?? 0;
  const centeredYPos = (windowHeight.value - popupHeight) / 2;

  initialYPos.value = Math.max(POPUP_VIEWPORT_MARGIN_PX, centeredYPos);
};

const onPickFile = () => {
  upload();
};

onClickOutside(profileAreasElement, () => {
  showProfileAreasDropdown.value = false;
});

const onClearFile = () => {
  clearFile();
};

const onViewFile = () => {
  if (!canPreviewSelectedPdf.value) return;
  showPopup();
  try {
    setPdf(fileContents.value, fileName.value || 'profile.pdf');
  } catch (error) {
    hidePopup();
  }
};

const closeAndReset = () => {
  isProfileFileHelpOpen.value = false;
  isInstructionsExpanded.value = false;
  showProfileAreasDropdown.value = false;
  reset();
  close();
};

// Closing mid-flight (via the X) prompts confirmation before aborting,
// since extractions can run long enough that an accidental click would
// otherwise throw away real work. Once confirmed, we cancel the in-flight
// run so its result can't land in the preview slot after dismissal.
const showCancelConfirm = ref(false);

const onCloseRequest = () => {
  if (isSubmitting.value) {
    showCancelConfirm.value = true;
    return;
  }
  closeAndReset();
};

// If extraction finishes while the cancel-confirm is open, dismiss the
// confirm — there's nothing left to cancel, and onSubmit will close the
// popup itself.
watch(isSubmitting, (busy) => {
  if (!busy) showCancelConfirm.value = false;
});

watch(
  [isOpen, windowHeight],
  () => {
    updateInitialYPos();
  },
  { immediate: true }
);

const onCancelConfirm = (confirmed: boolean) => {
  showCancelConfirm.value = false;
  if (confirmed) {
    cancel();
    closeAndReset();
  }
};

const onSubmit = async () => {
  const wasSuccessful = await submit();
  if (wasSuccessful) closeAndReset();
};

const showPrivacy = () => {
  setLegalText('privacy');
};

const stopAnalysisHintRotation = () => {
  if (analysisHintIntervalId !== null) {
    window.clearInterval(analysisHintIntervalId);
    analysisHintIntervalId = null;
  }
};

const startAnalysisHintRotation = () => {
  stopAnalysisHintRotation();
  currentAnalysisHintIndex.value = 0;

  if (analysisRunningHints.value.length <= 1) return;

  analysisHintIntervalId = window.setInterval(() => {
    currentAnalysisHintIndex.value = (currentAnalysisHintIndex.value + 1) % analysisRunningHints.value.length;
  }, ANALYSIS_HINT_ROTATION_INTERVAL_MS);
};

watch(
  [isSubmitting, analysisRunningHints],
  ([busy]) => {
    if (busy) {
      startAnalysisHintRotation();
      return;
    }

    stopAnalysisHintRotation();
    currentAnalysisHintIndex.value = 0;
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  stopAnalysisHintRotation();
});

return { __sfc: true,MAX_INSTRUCTION_CHARS, ANALYSIS_HINT_ROTATION_INTERVAL_MS, POPUP_WIDTH_PX, MAX_PDF_BYTES, POPUP_VIEWPORT_MARGIN_PX, t, locale, setLegalText, setPdf, showPopup, hidePopup, isOpen, close, fileName, fileContents, customInstructions, outputLanguage, selectedProfileAreas, isSubmitting, isReanalyzing, submit, cancel, reset, clearFile, fileUploadConfig, upload, pickedFileName, pickedFileContents, pickedMimeType, pickedFileError, maxFileSizeMB, isInstructionsExpanded, isProfileFileHelpOpen, showProfileAreasDropdown, charsLeft, previewButtonTitle, clearButtonTitle, pickFileButtonTitle, outputLanguageOptions, profileAreaItems, isAllProfileAreasSelected, selectedProfileAreasTitle, selectAllProfileAreas, selectNoProfileAreas, toggleProfileArea, fileErrorText, canPreviewSelectedPdf, currentAnalysisHintIndex, analysisHintIntervalId, analysisRunningHints, currentAnalysisRunningHint, popupContentElement, profileAreasElement, windowWidth, windowHeight, initialXPos, initialYPos, updateInitialYPos, onPickFile, onClearFile, onViewFile, closeAndReset, showCancelConfirm, onCloseRequest, onCancelConfirm, onSubmit, showPrivacy, stopAnalysisHintRotation, startAnalysisHintRotation, BCXPopup, BCXButton, BCXConfirm, BCXChevronSwitch, BCLoadingAnimation, BCXHelpTextTriggerIcon, BCXInfoCollapsible, BCXMarkdown, BCXSearchSelect, EditIcon, MarvinInfoBox, AiIcon, IconLightbulb, IconAdd, IconRemove }
}

})